import * as Sentry from "@sentry/react";
import { registerErrorReporter } from "../lib/logger";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;


export function sentryInit(): void {
  if (!DSN) {
    // No DSN → skip. Works fine in local dev with no .env entry.
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE, // "production" | "development" | "staging"
    release: import.meta.env.VITE_APP_VERSION as string | undefined,

    // ── Performance: disabled (keep it to 0 until you need it) ──────────────
    tracesSampleRate: 0,

    // ── Session Replay: opt-out ──────────────────────────────────────────────
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // ── GDPR: never attach PII automatically ────────────────────────────────
    sendDefaultPii: false,

    // ── Integrations: keep only exception capture ───────────────────────────
    // browserApiErrorsIntegration and globalHandlersIntegration are included by
    // default and cover window.onerror + unhandledrejection.
    
    integrations: (defaults) =>
      defaults.filter(
        (i) =>
          i.name !== "Replay" &&
          i.name !== "BrowserSession" &&
          i.name !== "HttpContext", // would attach URL + user-agent headers
      ),

    // ── Filter low-signal / noisy events ────────────────────────────────────
    beforeSend(event, hint) {
      // Drop non-error events that somehow slip through (e.g. breadcrumbs-only)
      if (!event.exception && event.level !== "error") return null;

      // Drop non-Error promise rejections (e.g. Firebase SDK rejecting with undefined)
      if (hint?.originalException === undefined || hint?.originalException === null) {
        return null;
      }

      // Drop known browser extension noise
      const msg =
        hint?.originalException instanceof Error
          ? hint.originalException.message
          : String(hint?.originalException ?? "");
      if (/ResizeObserver loop|Loading chunk|ChunkLoadError/i.test(msg)) {
        return null;
      }

      return event;
    },
  });

  // ── Wire logger.error → Sentry ─────────────────────────────────────────────
  // Only log.error() calls reach Sentry. debug/info/warn are ignored.
  registerErrorReporter((tag, message, data) => {
    Sentry.withScope((scope) => {
      scope.setTag("logger_tag", tag);
      if (data) scope.setExtras(data as Record<string, unknown>);
      Sentry.captureMessage(message, "error");
    });
  });
}

/**
 * Attach the authenticated user's opaque UID to all subsequent Sentry events.
 * Call this inside your auth state observer once the user is known.
 * Never pass email, name, or any other PII here.
 */
export function setSentryUser(uid: string | null): void {
  if (!DSN) return;
  if (uid) {
    Sentry.setUser({ id: uid });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Capture an Error object directly (e.g. from a catch block where you have
 * the real Error, not just a message string).
 */
export function captureError(err: unknown, context?: Record<string, unknown>): void {
  if (!DSN) return;
  Sentry.withScope((scope) => {
    if (context) scope.setExtras(context);
    Sentry.captureException(err);
  });
}

// Re-export the ErrorBoundary component so callers don't need to import
// @sentry/react directly.
export { Sentry };
